/**
 * Focus-gated background notification helpers, shared between the provider
 * (UPSERTED / onConversationDone / onApprovalRequested callbacks) and the
 * registry-owned liveness consumer (turn-complete firehose path).
 *
 * Lives in utils/ so conversation-liveness-consumer (also utils/) can import
 * without creating a cycle back into the provider component tree.
 */

import { useConversationRegistryStore } from '../stores/use-conversation-registry-store';
import { useAssistantNotificationsStore } from '../stores/use-assistant-notifications-store';

export const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Apply a background-conversation notification, gated on focus. A conversation
 * that IS focused renders completion/approval inline, so it gets no badge.
 * Exported for direct unit test of the not-focused gating (design §8.4).
 *
 * @param kind             'done' (quiet completion) | 'approval' (needs-you)
 * @param conversationId   the conversation the event is for (=== chatId in v2)
 * @param focusedId        the currently-focused conversation id, or null
 */
export function applyBackgroundNotification(
  kind: 'done' | 'approval',
  conversationId: string,
  focusedId: string | null,
): void {
  // Non-UUID ids (e.g. the workspace overlay's stable 'workspace-assistant'
  // constant) have no list entry that could ever clear the badge — skip them.
  if (!UUID_RE.test(conversationId)) return;
  if (conversationId === focusedId) return; // focused → inline, no badge
  const store = useAssistantNotificationsStore.getState();
  if (kind === 'done') store.markDone(conversationId);
  else store.markNeedsApproval(conversationId);
}

/**
 * Wiring helper: reads focusedConversationId from the registry store and
 * calls applyBackgroundNotification('done', ...). Used by the provider's
 * onConversationDone callback and the liveness consumer's turn-complete branch.
 */
export function applyConversationDoneBadge(conversationId: string): void {
  const focusedId = useConversationRegistryStore.getState().focusedConversationId;
  applyBackgroundNotification('done', conversationId, focusedId);
}

/**
 * Wiring helper: reads focusedConversationId from the registry store and
 * calls applyBackgroundNotification('approval', ...). Used by the provider's
 * onApprovalRequested callback and the liveness consumer's turn-complete branch.
 */
export function applyApprovalBadge(chatId: string): void {
  const focusedId = useConversationRegistryStore.getState().focusedConversationId;
  applyBackgroundNotification('approval', chatId, focusedId);
}
